import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import Seo from "@/components/Seo";
import AuthShell from "@/components/AuthShell";
import GoogleButton from "@/components/GoogleButton";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiErrorDetail } from "@/lib/api";
import { track } from "@/lib/analytics";

const inputCls =
  "w-full rounded-xl border border-border bg-card px-4 py-3 text-sm outline-none transition-colors focus:border-primary";

export default function Signup() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [consents, setConsents] = useState({ terms: false, marketing: false, showcase: false });
  const [loading, setLoading] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const toggleConsent = (k) => (e) => setConsents((c) => ({ ...c, [k]: e.target.checked }));

  const submit = async (e) => {
    e.preventDefault();
    if (form.password.length < 8) {
      toast.error("Password must be at least 8 characters.");
      return;
    }
    if (!consents.terms) {
      toast.error("Please accept the Terms & Privacy Policy to continue.");
      return;
    }
    setLoading(true);
    try {
      await register(form.name, form.email, form.password, consents);
      track("signup", { method: "email", marketing_opt_in: consents.marketing, showcase_opt_in: consents.showcase });
      toast.success("Account created — welcome to Workspace!");
      navigate("/app");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Signup failed.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Seo title="Start Free" description="Create your free Stupeco WorkSpace account." path="/signup" />
      <AuthShell
        title="Start free"
        subtitle="Create your account and be up and running in under two minutes."
        footer={<>Already have an account? <Link to="/login" className="font-medium text-foreground hover:underline" data-testid="go-login-link">Log in</Link></>}
      >
        <GoogleButton label="Sign up with Google" />
        <div className="my-6 flex items-center gap-4 text-xs text-muted-foreground">
          <span className="h-px flex-1 bg-border" /> or <span className="h-px flex-1 bg-border" />
        </div>
        <form onSubmit={submit} className="space-y-4" data-testid="signup-form">
          <div>
            <label className="mb-1.5 block text-sm font-medium">Full name</label>
            <input required value={form.name} onChange={update("name")} className={inputCls} data-testid="signup-name-input" placeholder="Jane Founder" />
          </div>
          <div>
            <label className="mb-1.5 block text-sm font-medium">Email</label>
            <input required type="email" value={form.email} onChange={update("email")} className={inputCls} data-testid="signup-email-input" placeholder="you@company.com" />
          </div>
          <div>
            <label className="mb-1.5 block text-sm font-medium">Password</label>
            <input required type="password" value={form.password} onChange={update("password")} className={inputCls} data-testid="signup-password-input" placeholder="At least 8 characters" />
          </div>

          {/* Consent block — one required, two optional (unchecked by default) */}
          <div className="space-y-2.5 rounded-2xl border border-border bg-background/40 p-4" data-testid="signup-consents">
            <ConsentRow
              testid="consent-terms"
              required
              checked={consents.terms}
              onChange={toggleConsent("terms")}
              label={<>I agree to the StUpEco Workspace <Link to="/legal/terms" className="underline hover:text-foreground">Terms &amp; Conditions</Link> and <Link to="/legal/privacy" className="underline hover:text-foreground">Privacy Policy</Link>.</>}
            />
            <ConsentRow
              testid="consent-marketing"
              checked={consents.marketing}
              onChange={toggleConsent("marketing")}
              label="Send me product updates, new feature announcements, useful resources and promotional communications."
            />
            <ConsentRow
              testid="consent-showcase"
              checked={consents.showcase}
              onChange={toggleConsent("showcase")}
              label="I consent to StUpEco identifying my business/profile as a user of StUpEco Workspace in promotional, community or showcase materials."
            />
          </div>

          <button
            type="submit"
            disabled={loading || !consents.terms}
            data-testid="signup-submit-btn"
            className="w-full rounded-full brand-gradient-bg px-6 py-3.5 text-sm font-semibold text-white transition-all hover:-translate-y-0.5 hover:shadow-lg disabled:cursor-not-allowed disabled:opacity-60"
          >
            {loading ? "Creating account…" : "Create free account"}
          </button>
          <p className="text-center text-xs text-muted-foreground">You can withdraw the optional consents any time from Settings.</p>
        </form>
      </AuthShell>
    </>
  );
}

function ConsentRow({ testid, checked, onChange, label, required = false }) {
  return (
    <label className="flex cursor-pointer items-start gap-2.5 text-xs leading-relaxed text-muted-foreground" data-testid={testid}>
      <input
        type="checkbox"
        checked={checked}
        onChange={onChange}
        required={required}
        data-testid={`${testid}-input`}
        className="mt-0.5 h-4 w-4 shrink-0 rounded border-border text-primary focus:ring-primary"
      />
      <span>
        {label}
        {required && <span className="ml-1 text-brand-red">*</span>}
      </span>
    </label>
  );
}
